import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lightbulb, KeyRound, Loader2, CheckSquare, ChevronDown } from 'lucide-react';
import { apiClient } from '../api/client.ts';
import { ALLOWED_EMAIL_DOMAINS } from '@cg-idea/common';

export function Login() {
  const navigate = useNavigate();
  const DOMAINS = ALLOWED_EMAIL_DOMAINS;

  // Remember Me state loading from localStorage
  const [rememberMe, setRememberMe] = useState(() => localStorage.getItem('rememberMe') === 'true');
  const [username, setUsername] = useState(() => localStorage.getItem('rememberUsername') || '');
  const [domain, setDomain] = useState(() => localStorage.getItem('rememberDomain') || DOMAINS[0]);

  // Step state: EMAIL or PIN or LINK_SENT
  const [step, setStep] = useState<'EMAIL' | 'PIN' | 'LINK_SENT'>('EMAIL');
  const [email, setEmail] = useState('');
  const [hasPin, setHasPin] = useState(false); // Does the email already have a PIN?
  const [pin, setPin] = useState(''); // 4-digit PIN input

  // UX states
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [emailError, setEmailError] = useState('');

  const onEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim()) {
      setEmailError('Username is required');
      return;
    }
    const fullEmail = `${username.trim().toLowerCase()}@${domain}`;
    setEmail(fullEmail);

    setLoading(true);
    setErrorMessage('');
    setInfoMessage('');
    setEmailError('');

    try {
      // 1. Check PIN Status
      const statusRes = await apiClient.post('/auth/check-pin-status', { email: fullEmail });
      const { exists, hasPin: emailHasPin } = statusRes.data.data;

      if (!exists) {
        setErrorMessage(
          'This email is not registered in our employee registry. Please select a valid employee when submitting your first idea, or contact your admin.'
        );
        setLoading(false);
        return;
      }

      setHasPin(emailHasPin);

      if (emailHasPin) {
        // User already has a persistent PIN
        setStep('PIN');
        const savedPin = localStorage.getItem(`savedPin-${fullEmail}`);
        if (savedPin) {
          setPin(savedPin);
        } else {
          setPin('');
        }
      } else {
        // First login, send setup link
        await apiClient.post('/auth/request-pin-setup', {
          email: fullEmail,
          frontendUrl: window.location.origin
        });
        setStep('LINK_SENT');
      }
    } catch (err: any) {
      setErrorMessage(
        err.response?.data?.error?.message || 'Failed to sign in. Please verify your username.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResetPin = async () => {
    setLoading(true);
    setErrorMessage('');
    setInfoMessage('');
    try {
      await apiClient.post('/auth/request-pin-setup', {
        email,
        frontendUrl: window.location.origin
      });
      setStep('LINK_SENT');
    } catch (err: any) {
      setErrorMessage(
        err.response?.data?.error?.message || 'Failed to dispatch reset link.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResetPinFromEmailStep = async () => {
    if (!username.trim()) {
      setEmailError('Please enter your username first');
      return;
    }
    const fullEmail = `${username.trim().toLowerCase()}@${domain}`;
    setEmail(fullEmail);

    setLoading(true);
    setErrorMessage('');
    setInfoMessage('');
    setEmailError('');

    try {
      await apiClient.post('/auth/request-pin-setup', {
        email: fullEmail,
        frontendUrl: window.location.origin
      });
      setStep('LINK_SENT');
    } catch (err: any) {
      setErrorMessage(
        err.response?.data?.error?.message || 'Failed to dispatch reset link.'
      );
    } finally {
      setLoading(false);
    }
  };

  const onPinSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pin.length !== 4) return;

    setLoading(true);
    setErrorMessage('');
    setInfoMessage('');
    try {
      const res = await apiClient.post('/auth/verify', { email, pin });
      const { accessToken, user } = res.data.data;

      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('userId', user.id);
      localStorage.setItem('userName', user.name);
      localStorage.setItem('userEmail', user.email);
      localStorage.setItem('userRole', user.role);
      localStorage.setItem('userSubCoreRole', user.subCoreRole || '');
      if (user.department) {
        localStorage.setItem('userDepartment', user.department);
      }
      if (user.mpId) {
        localStorage.setItem('mpId', user.mpId);
      }

      const isProfileComplete = !!(user.name && user.mpId && user.department);
      localStorage.setItem('isProfileComplete', isProfileComplete ? 'true' : 'false');

      // Process "Remember Me"
      if (rememberMe) {
        localStorage.setItem('rememberUsername', username);
        localStorage.setItem('rememberDomain', domain);
        localStorage.setItem('rememberMe', 'true');
        localStorage.setItem(`savedPin-${email}`, pin);
      } else {
        localStorage.removeItem('rememberUsername');
        localStorage.removeItem('rememberDomain');
        localStorage.setItem('rememberMe', 'false');
        localStorage.removeItem(`savedPin-${email}`);
      }

      if (user.role === 'ADMIN' || user.role === 'APPROVAL_TEAM') {
        navigate('/admin');
      } else if (user.role === 'LEADER') {
        navigate('/leader');
      } else if (user.role === 'COMMITTEE_HEAD') {
        navigate('/committee-head');
      } else if (user.role === 'COMMITTEE_MEMBER') {
        navigate('/committee-member');
      } else if (user.role === 'EXTERNAL_COMMITTEE') {
        navigate('/external-committee');
      } else if (user.role === 'EXECUTION_COMMITTEE') {
        navigate('/execution-committee');
      } else if (user.role === 'MIS_COMMITTEE') {
        navigate('/mis');
      } else {
        navigate('/dashboard');
      }
    } catch (err: any) {
      setErrorMessage(
        err.response?.data?.error?.message || 'PIN passcode verification failed. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-white font-sans text-slate-800 overflow-x-hidden">
      {/* Left Pane - Branding */}
      <div className="hidden lg:flex lg:w-5/12 bg-[#0B1A30] flex-col justify-between p-12 relative overflow-hidden">
        <div className="relative z-10 flex flex-col items-center justify-center h-full text-center">
          <div className="w-80 h-80 mb-12 relative flex items-center justify-center">
            <div className="absolute inset-0 bg-blue-500/20 rounded-full blur-3xl"></div>
            <div className="relative z-10 flex flex-col items-center">
              <Lightbulb className="w-24 h-24 text-amber-400 mb-6 drop-shadow-[0_0_15px_rgba(251,191,36,0.5)]" />
              <div className="w-48 h-2 bg-blue-500/50 rounded-full mt-4 blur-[2px]"></div>
            </div>
          </div>

          <h1 className="text-4xl font-bold text-white mb-4 tracking-wide font-display">Idea Management Portal</h1>
          <p className="text-blue-200 text-lg max-w-sm">Share your ideas, improve continuously, create value.</p>
        </div>
        <div className="relative z-10 text-center">
          <p className="text-blue-400/50 text-sm">© {new Date().getFullYear()} Idea Management Portal. All rights reserved.</p>
        </div>
      </div>

      {/* Right Pane - Login Form */}
      <div className="flex-1 flex flex-col justify-center items-center p-6 sm:p-12 w-full max-w-[100vw]">
        <div className="w-full max-w-md">
          <div className="mb-10 text-center lg:text-left">
            <h2 className="text-3xl font-bold text-slate-900 font-display mb-2">Welcome Back!</h2>
            <p className="text-slate-500">Sign in to track your idea submissions</p>
          </div>

          {errorMessage && (
            <div className="mb-6 bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded text-sm shadow-sm">
              {errorMessage}
            </div>
          )}

          {infoMessage && (
            <div className="mb-6 bg-emerald-50 border-l-4 border-emerald-500 text-emerald-700 p-4 rounded text-sm shadow-sm">
              {infoMessage}
            </div>
          )}

          {step === 'EMAIL' ? (
            <form onSubmit={onEmailSubmit} className="space-y-6">
              <div>
                <label className="block text-slate-700 text-sm font-semibold mb-2">Company Email</label>
                <div className="relative">
                  <div className="flex items-center w-full bg-white border border-slate-300 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 rounded-xl transition-all relative overflow-hidden">
                    <input
                      type="text"
                      placeholder="username"
                      value={username}
                      onChange={(e) => {
                        let val = e.target.value;
                        if (val.includes('@')) {
                          const parts = val.split('@');
                          val = parts[0];
                          const domainPart = parts[1]?.toLowerCase();
                          const matchedDomain = DOMAINS.find(d => d.toLowerCase() === domainPart);
                          if (matchedDomain) {
                            setDomain(matchedDomain);
                          }
                        }
                        setUsername(val);
                        if (emailError) setEmailError('');
                      }}
                      className="flex-1 min-w-0 px-3 sm:px-4 py-3 bg-transparent text-slate-800 placeholder-slate-400 focus:outline-none rounded-l-xl text-sm sm:text-base"
                    />
                    <div className="flex items-center pr-2 sm:pr-3 border-l border-slate-200 shrink-0">
                      <span className="text-slate-500 pl-2 sm:pl-3 mr-1 text-sm sm:text-base">@</span>
                      <button
                        type="button"
                        onClick={() => setShowDropdown(!showDropdown)}
                        className="flex items-center text-slate-700 hover:text-slate-900 focus:outline-none py-2 whitespace-nowrap bg-transparent border-0 cursor-pointer text-sm sm:text-base"
                      >
                        {domain} <ChevronDown className="w-4 h-4 ml-1 text-slate-400 shrink-0" />
                      </button>
                    </div>
                  </div>

                  {/* Dropdown Menu */}
                  {showDropdown && (
                    <div className="absolute right-0 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg z-50 py-1">
                      {DOMAINS.map((d) => (
                        <button
                          key={d}
                          type="button"
                          className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-100 transition-colors border-0 bg-transparent cursor-pointer"
                          onClick={() => {
                            setDomain(d);
                            setShowDropdown(false);
                          }}
                        >
                          {d}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {emailError && (
                  <p className="text-red-500 text-xs mt-1.5">{emailError}</p>
                )}
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center space-x-2 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="hidden"
                  />
                  <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${rememberMe ? 'bg-blue-600 border-blue-600' : 'border-slate-300 group-hover:border-blue-400'}`}>
                    {rememberMe && <CheckSquare className={`w-3 h-3 text-white ${rememberMe ? 'block' : 'hidden'}`} />}
                  </div>
                  <span className="text-sm text-slate-600 select-none">Remember me</span>
                </label>
                <button
                  type="button"
                  onClick={handleResetPinFromEmailStep}
                  disabled={loading}
                  className="text-sm text-blue-600 font-semibold hover:underline bg-transparent border-0 cursor-pointer disabled:opacity-50"
                >
                  Forgot PIN?
                </button>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#1C4ED8] hover:bg-blue-700 text-white font-bold py-3.5 px-4 rounded-xl text-sm flex items-center justify-center transition-all disabled:opacity-70 shadow-md shadow-blue-600/20 border-0 cursor-pointer"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <span>Continue</span>
                )}
              </button>
            </form>
          ) : step === 'PIN' ? (
            <form onSubmit={onPinSubmit} className="space-y-6">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-slate-700 text-sm font-semibold">
                    {hasPin ? 'Enter Login PIN' : 'Enter 4-Digit Passcode'}
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      setStep('EMAIL');
                      setPin('');
                    }}
                    className="text-xs text-blue-600 font-semibold hover:underline bg-transparent border-0 cursor-pointer"
                  >
                    Change Email
                  </button>
                </div>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center text-slate-400">
                    <KeyRound className="w-5 h-5" />
                  </span>
                  <input
                    type="password"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={4}
                    placeholder="••••"
                    value={pin}
                    onChange={(e) => setPin(e.target.value.replace(/[^0-9]/g, ''))}
                    className="w-full pl-11 pr-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-800 placeholder-slate-300 text-center tracking-[12px] text-xl font-bold focus:border-blue-600 focus:ring-1 focus:ring-blue-600 focus:outline-none transition-all"
                  />
                </div>
                <div className="flex justify-between items-center mt-3">
                  <p className="text-xs text-slate-500">
                    Email: <strong className="text-slate-700">{email}</strong>
                  </p>
                  <button
                    type="button"
                    onClick={handleResetPin}
                    disabled={loading}
                    className="text-xs text-blue-600 font-semibold hover:underline bg-transparent border-0 cursor-pointer disabled:opacity-50"
                  >
                    Reset PIN
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || pin.length !== 4}
                className="w-full bg-[#1C4ED8] hover:bg-blue-700 text-white font-bold py-3.5 px-4 rounded-xl text-sm flex items-center justify-center transition-all disabled:opacity-50 shadow-md shadow-blue-600/20 border-0 cursor-pointer"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <span>Verify & Sign In</span>}
              </button>
            </form>
          ) : step === 'LINK_SENT' ? (
            <div className="text-center space-y-6">
              <div className="bg-blue-50 border border-blue-100 p-6 rounded-xl">
                <CheckSquare className="w-12 h-12 text-blue-500 mx-auto mb-4" />
                <h3 className="text-lg font-bold text-slate-800 mb-2">Check Your Email</h3>
                <p className="text-slate-600 text-sm">
                  We have sent a secure magic link to <strong className="text-slate-800">{email}</strong>.
                  Click the link in the email to set up your PIN and log in.
                </p>
              </div>
              <div className="flex flex-col space-y-3 pt-2">
                <button
                  type="button"
                  onClick={handleResetPin}
                  disabled={loading}
                  className="w-full bg-[#1C4ED8] hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-xl text-sm flex items-center justify-center transition-all disabled:opacity-50 shadow-md shadow-blue-600/20 border-0 cursor-pointer"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <span>Resend Link</span>}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setStep('EMAIL');
                    setPin('');
                  }}
                  disabled={loading}
                  className="text-sm text-slate-500 font-semibold hover:text-slate-800 transition-colors bg-transparent border-0 cursor-pointer"
                >
                  Back to Login
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
